import React, { useState, useEffect, useRef, useMemo } from 'react';

import {
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Portal,
} from '@chakra-ui/react';

import { CiCalendar } from 'react-icons/ci';

import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';

import './DateField.css';

const DateField = ({
  label,
  date,
  onChange,
  onOutOfRange,
  required,
  htmlID,
  isDisabled,
  isClearable = false,
  minDate,
  maxDate,
  isInvalid,
  labelMl = '0',
  ...rest
}: {
  label: string;
  date: Date | null;
  onChange: any;
  onOutOfRange?: (isOutOfRange: boolean) => void;
  required?: boolean;
  htmlID?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  minDate?: Date;
  maxDate?: Date;
  isInvalid?: boolean;
  labelMl?: string;
  [x: string]: any;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const dateTimestamp = date?.getTime() ?? null;
  const stableDate = useMemo(
    () => (dateTimestamp !== null ? new Date(dateTimestamp) : null),
    [dateTimestamp],
  );

  // Handle clicks outside both the input container and the picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (stableDate) {
      const year = stableDate.getFullYear();
      const month = String(stableDate.getMonth() + 1).padStart(2, '0');
      const day = String(stableDate.getDate()).padStart(2, '0');
      setInputValue(`${year}-${month}-${day}`);
    } else {
      setInputValue('');
    }
  }, [stableDate]);

  const formatInput = (value: string) => {
    const numbers = value.replace(/[^0-9]/g, '');

    if (numbers.length <= 4) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
    } else {
      return `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6, 8)}`;
    }
  };

  const parseDate = (value: string): Date | null => {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{1,2})$/);
    if (!match) return null;

    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);

    if (month < 1 || month > 12 || day < 1 || day > 31) return null;

    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  };

  const isDateInRange = (d: Date): boolean => {
    if (minDate && d < minDate) return false;
    if (maxDate && d > maxDate) return false;
    return true;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatInput(e.target.value);
    setInputValue(formatted);

    if (formatted.length === 9) {
      const parsedDate = parseDate(formatted);
      if (parsedDate && !isDateInRange(parsedDate)) {
        onOutOfRange?.(true);
      } else {
        onOutOfRange?.(false);
      }
    } else if (formatted.length === 10) {
      const parsedDate = parseDate(formatted);
      if (parsedDate) {
        if (isDateInRange(parsedDate)) {
          onOutOfRange?.(false);
          onChange(parsedDate);
        } else {
          onOutOfRange?.(true);
        }
      } else {
        onOutOfRange?.(false);
      }
    } else if (formatted.length === 0) {
      onOutOfRange?.(false);
      onChange(null);
    }
  };

  const handleBlur = () => {
    if (inputValue.length === 9) {
      const parsedDate = parseDate(inputValue);
      if (parsedDate && isDateInRange(parsedDate)) {
        onChange(parsedDate);
      }
    }
  };

  const handleDatePickerChange = (selectedDate: Date | null) => {
    if (selectedDate && !isDateInRange(selectedDate)) {
      return;
    }
    onOutOfRange?.(false);
    onChange(selectedDate);
    setIsOpen(false);
  };

  const handleCalendarClick = () => {
    if (!isDisabled) {
      if (!isOpen && inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setPickerPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
        });
      }
      setIsOpen(!isOpen);
    }
  };

  const handleInputClick = () => {
    if (!isDisabled) {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setPickerPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
        });
      }
      setIsOpen(true);
    }
  };

  return (
    <FormControl id={htmlID} isRequired={required} isDisabled={isDisabled}>
      <FormLabel htmlFor={htmlID} ml={labelMl}>
        {label}
      </FormLabel>
      <div
        ref={containerRef}
        className="custom-date-picker"
        style={{ position: 'relative' }}
      >
        <InputGroup>
          <Input
            ref={inputRef}
            variant="main"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onClick={handleInputClick}
            placeholder="YYYY-MM-DD"
            disabled={isDisabled}
            title={isDisabled ? 'Editing is disabled' : ''}
            borderColor={
              isInvalid || (!inputValue && required) ? 'red.600' : 'inherit'
            }
            maxLength={10}
            {...rest}
          />
          <InputRightElement width="auto" pr={2} zIndex={0}>
            <IconButton
              aria-label="Open calendar"
              icon={<CiCalendar size={20} />}
              size="sm"
              variant="ghost"
              bg="white"
              onClick={handleCalendarClick}
              isDisabled={isDisabled}
            />
          </InputRightElement>
        </InputGroup>
        {isOpen && (
          <Portal>
            <div
              ref={pickerRef}
              className="custom-date-picker"
              style={{
                position: 'absolute',
                top: pickerPosition.top,
                left: pickerPosition.left,
                zIndex: 1400,
              }}
            >
              {/* @ts-ignore */}
              <DatePicker
                selected={date}
                onChange={handleDatePickerChange}
                inline
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                yearDropdownItemNumber={100}
                scrollableYearDropdown
                dateFormat="yyyy-MM-dd"
                isClearable={isClearable && !isDisabled}
                placeholderText="Select a date"
                minDate={minDate}
                maxDate={maxDate}
              />
            </div>
          </Portal>
        )}
      </div>
    </FormControl>
  );
};

export default DateField;
